import { inject, Injectable } from '@angular/core';
import { EMPTY, filter, map, Observable, of, switchMap, take, tap } from 'rxjs';
import {
  BodyAreaCodeDto,
  MhsConfigurationApiClient,
  NhfDataCodeGroupType,
} from '../api/mhsConfigurationApiClient';
import { AuthStore } from '../authentication/authStore';
import {
  STORAGE_BODY_AREAS,
  STORAGE_KEY_NHF_CODE_ADDITIONAL_CITERIAL,
  STORAGE_KEY_RESOURCE_IDENTITY_TYPES,
  STORAGE_KEY_RESOURCE_MED_SPECIALIZATIONS,
  STORAGE_KEY_RESOURCE_MED_TITLES,
} from '../context/storage-keys';
import { StorageService } from '../context/storageService';
import { UsertStore } from '../context/userStore';
import { NhfCodeBase } from './dto/nhf-code-base';
import { GenericResource } from './dto/resource-base';
import { ResourceIdentityType } from './dto/resource-identity-type';
import { ResourceMedSpecialization } from './dto/resource-med-specialization';
import { ResourceMedTitle } from './dto/resource-med-title';
@Injectable({
  providedIn: 'root',
})
export class ResourceService {
  configurationApiClient = inject(MhsConfigurationApiClient);
  localStorageService = inject(StorageService);
  authStore = inject(AuthStore);
  userStore = inject(UsertStore);

  getMedTitles(): Observable<ResourceMedTitle[]> {
    return this.getGenericResource<ResourceMedTitle>('Title', STORAGE_KEY_RESOURCE_MED_TITLES);
  }
  getMedSpecializations(): Observable<ResourceMedSpecialization[]> {
    return this.getGenericResource<ResourceMedSpecialization>(
      'Specialisation',
      STORAGE_KEY_RESOURCE_MED_SPECIALIZATIONS,
    );
  }
  getResourceIdentityTypes(): Observable<ResourceIdentityType[]> {
    return this.getGenericResource<ResourceIdentityType>(
      'Identity',
      STORAGE_KEY_RESOURCE_IDENTITY_TYPES,
    );
  }
  getNhfCodeAdditionalCriterial(): Observable<NhfCodeBase[]> {
    return this.getGenericNhfCode<NhfCodeBase>(
      NhfDataCodeGroupType.AdditionalCriteriaType,
      STORAGE_KEY_NHF_CODE_ADDITIONAL_CITERIAL,
    );
  }
  getGenericResource<T extends GenericResource>(
    resourceType: string,
    cacheKey: string,
  ): Observable<T[]> {
    if (this.authStore.accessToken() === '') {
      return EMPTY;
    }
    if (this.localStorageService.get(cacheKey)) {
      const resources = JSON.parse(this.localStorageService.get(cacheKey) || '[]');
      return of(resources);
    }
    return this.userStore.currentTenant$.pipe(
      filter(Boolean),
      take(1),
      switchMap((tenant) => {
        const tenantName = tenant?.tenantName;
        return this.configurationApiClient.entry_GetByScope(resourceType).pipe(
          map((entries) =>
            entries.map(
              (e) =>
                ({
                  name: e.entryKey!,
                  description: e.entryDefaultValue!,
                }) as T,
            ),
          ),
          tap((resources) => {
            //this.avilableTenants.set(tenants);
            this.localStorageService.set(cacheKey, JSON.stringify(resources));
          }),
        );
      }),
    );
  }
  getGenericNhfCode<T extends NhfCodeBase>(
    groupType: NhfDataCodeGroupType,
    cacheKey: string,
  ): Observable<T[]> {
    if (this.authStore.accessToken() === '') {
      return EMPTY;
    }
    if (this.localStorageService.get(cacheKey)) {
      const resources = JSON.parse(this.localStorageService.get(cacheKey) || '[]');
      return of(resources);
    }
    return this.userStore.currentTenant$.pipe(
      filter(Boolean),
      take(1),
      switchMap((tenant) => {
        const tenantName = tenant?.tenantName;
        return this.configurationApiClient.nhfDataCode_Get(groupType).pipe(
          map((entries) =>
            entries.map(
              (e) =>
                ({
                  code: e.code!,
                  name: e.name!,
                }) as T,
            ),
          ),
          tap((resources) => {
            //this.avilableTenants.set(tenants);
            this.localStorageService.set(cacheKey, JSON.stringify(resources));
          }),
        );
      }),
    );
  }
  getBodyAreas(): Observable<BodyAreaCodeDto[]> {
    if (this.authStore.accessToken() === '') {
      return EMPTY;
    }
    if (this.localStorageService.get(STORAGE_BODY_AREAS)) {
      const resources = JSON.parse(this.localStorageService.get(STORAGE_BODY_AREAS) || '[]');
      return of(resources);
    }
    return this.configurationApiClient.bodyArea_GetAll().pipe(
      tap((resources) => {
        this.localStorageService.set(STORAGE_BODY_AREAS, JSON.stringify(resources));
      }),
    );
  }
}
